"use client";

import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";

// Security: html comes exclusively from Saleor's admin CMS (productDescriptionHtml),
// the same source already rendered via dangerouslySetInnerHTML in the parent Server Component.
// No user-generated content reaches this prop.

const COLLAPSED_HEIGHT = 160;

interface PdpDescriptionCollapsibleProps {
	html: string;
}

export function PdpDescriptionCollapsible({ html }: PdpDescriptionCollapsibleProps) {
	const t = useTranslations("pdpUx.longForm");
	// Default open: product copy is the primary PDP content; collapse is optional.
	const [isExpanded, setIsExpanded] = useState(true);
	const [collapseState, setCollapseState] = useState<{ needsCollapse: boolean; height: number | "auto" }>({
		needsCollapse: false,
		height: "auto",
	});
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = contentRef.current;
		if (!el) return;
		const observer = new ResizeObserver(() => {
			const fullHeight = el.scrollHeight;
			const canCollapse = fullHeight > COLLAPSED_HEIGHT + 40;
			setCollapseState(
				canCollapse
					? { needsCollapse: true, height: isExpanded ? fullHeight : COLLAPSED_HEIGHT }
					: { needsCollapse: false, height: "auto" },
			);
		});
		observer.observe(el);
		return () => observer.disconnect();
		// Re-measure when expand state changes so height tracks open vs collapsed.
	}, [html, isExpanded]);

	const toggle = () => {
		const el = contentRef.current;
		if (!el) return;
		if (isExpanded) {
			setCollapseState({ needsCollapse: true, height: COLLAPSED_HEIGHT });
			setIsExpanded(false);
		} else {
			setCollapseState({ needsCollapse: true, height: el.scrollHeight });
			setIsExpanded(true);
		}
	};

	const { needsCollapse, height } = collapseState;

	return (
		<div>
			<div
				className="relative overflow-hidden transition-[height] duration-500 ease-out-soft"
				style={{ height: needsCollapse ? height : "auto" }}
			>
				<div
					ref={contentRef}
					className={[
						"text-foreground/85 space-y-4 font-sans text-base leading-relaxed",
						"[&_*]:!font-sans",
						"[&_h1]:!font-display [&_h2]:!font-display [&_h3]:!font-display [&_h4]:!font-display",
						"[&_h2]:mb-2 [&_h2]:mt-6 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-foreground",
						"[&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:tracking-tight [&_h3]:text-foreground",
						"[&_h4]:mb-1.5 [&_h4]:mt-4 [&_h4]:text-base [&_h4]:font-semibold [&_h4]:text-foreground",
						"[&_p]:text-foreground/85 [&_p]:leading-relaxed",
						"[&_ul]:my-3 [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-5",
						"[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:space-y-1.5 [&_ol]:pl-5",
						"[&_li]:leading-relaxed",
						"[&_table]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm",
						"[&_th]:border-border/60 [&_th]:bg-muted/40 [&_th]:border [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-medium",
						"[&_td]:border-border/60 [&_td]:border [&_td]:px-3 [&_td]:py-2 [&_td]:align-top",
						"[&_tr:nth-child(even)_td]:bg-muted/20",
					].join(" ")}
					dangerouslySetInnerHTML={{ __html: html }}
				/>
				{needsCollapse && !isExpanded && (
					<div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-card to-transparent" />
				)}
			</div>

			{needsCollapse && (
				<button
					type="button"
					onClick={toggle}
					className="hover:text-primary/80 mt-3 flex items-center gap-1.5 text-sm font-medium text-primary transition-colors"
				>
					{isExpanded ? t("showLess") : t("showMore")}
					<ChevronDown
						className={`h-4 w-4 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
					/>
				</button>
			)}
		</div>
	);
}
